import { useQuery } from '@tanstack/react-query';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, useSearchParams } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import type { Album } from '../albums/api';
import type { TrackSummary } from '../tracks/TrackList';
import { useDocumentTitle } from '../../lib/title';

interface Person {
  id: number;
  username: string;
  display_name: string | null;
  bio: string | null;
  follower_count: number;
  avatar: string | null;
}

interface Results {
  query: string;
  type: 'all' | 'tracks' | 'users' | 'collections';
  /** True when the query is too short for the full-text index. */
  short_query: boolean;
  tracks: TrackSummary[];
  users: Person[];
  collections: Album[];
}

const TABS = [
  { id: 'all', label: 'Everything' },
  { id: 'tracks', label: copy['noun.track.plural'] },
  { id: 'users', label: 'People' },
  { id: 'collections', label: copy['noun.collection.plural'] },
] as const;

/**
 * Search (HANDOFF §7).
 *
 * The query lives in the URL, so a set of results is something you can send to
 * somebody or come back to — and so the back button works the way people expect
 * after clicking into a result.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();

  const query = params.get('q') ?? '';
  const type = (params.get('type') ?? 'all') as Results['type'];

  useDocumentTitle(query.trim() ? `Search: ${query.trim()}` : 'Search');

  const { data, isPending, isError } = useQuery<Results>({
    queryKey: ['search', query, type],
    queryFn: () => api.get<Results>(`/search?q=${encodeURIComponent(query)}&type=${type}`),
    // No request until there is something to search for. Otherwise landing on
    // the page fires a query for the empty string.
    enabled: query.trim() !== '',
  });

  const setType = (next: string) => {
    const updated = new URLSearchParams(params);
    updated.set('type', next);
    setParams(updated, { replace: true });
  };

  const total = (data?.tracks.length ?? 0) + (data?.users.length ?? 0) + (data?.collections.length ?? 0);

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">
        {query ? <>Results for “{query}”</> : 'Search'}
      </h1>

      {query === '' ? (
        <p className="mt-2 text-sm text-soft">
          Look for a track, an artist, or an album. Try{' '}
          <Link to="/discover" className="text-accent hover:underline">
            {copy['nav.discover']}
          </Link>{' '}
          if you are not sure what you are after.
        </p>
      ) : (
        <>
          <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line pb-3">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={type === tab.id}
                data-search-tab={tab.id}
                onClick={() => setType(tab.id)}
                className={`rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
                  type === tab.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/*
            Said out loud, because otherwise a two-letter search looks like
            "there is nothing called that" rather than "the index cannot help
            with words this short".
          */}
          {data?.short_query && (
            <p className="mt-4 text-xs text-faint" data-short-query>
              Short searches match titles and handles that <em>start</em> with what you typed. Try
              three letters or more for a full search.
            </p>
          )}

          {isPending ? (
            <div className="mt-6 flex flex-col gap-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : isError ? (
            <Card className="mt-6 p-6">
              <p className="text-sm text-soft">That search could not be run.</p>
            </Card>
          ) : total === 0 ? (
            <Card className="mt-6 p-6">
              <p className="text-sm text-soft" data-no-results>
                Nothing matched “{query}”.
              </p>
            </Card>
          ) : (
            <div className="mt-6 flex flex-col gap-8" data-search-results>
              {data.users.length > 0 && <People people={data.users} />}
              {data.tracks.length > 0 && <Tracks tracks={data.tracks} />}
              {data.collections.length > 0 && <Albums albums={data.collections} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function People({ people }: { people: Person[] }) {
  return (
    <section>
      <h2 className="meta-caps mb-3 text-faint">People</h2>
      <ul className="flex flex-col gap-2" data-search-users>
        {people.map((person) => (
          <li key={person.id} data-search-user={person.username}>
            <Link to={`/${person.username}`} className="block">
              <Card className="flex items-center gap-4 p-4 transition-colors hover:border-line-lit">
                <Avatar src={person.avatar} name={person.display_name || person.username} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    {person.display_name || person.username}
                  </p>
                  <p className="meta-caps truncate">
                    @{person.username}
                    {person.follower_count > 0 && (
                      <> · {person.follower_count.toLocaleString()} {person.follower_count === 1
                        ? copy['noun.follower']
                        : copy['noun.follower.plural']}</>
                    )}
                  </p>
                </div>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Tracks({ tracks }: { tracks: TrackSummary[] }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  return (
    <section>
      <h2 className="meta-caps mb-3 text-faint">{copy['noun.track.plural']}</h2>
      <ul className="flex flex-col gap-2" data-search-tracks>
        {tracks.map((track) => {
          const isCurrent = current?.id === track.id;

          return (
            <li key={track.id} data-search-track={track.id}>
              <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                <button
                  type="button"
                  aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                  // `search` is why the enum has that value (§6.3) - knowing
                  // which plays came from a search is how you find out whether
                  // search is working.
                  onClick={() => (isCurrent ? toggle() : void play(track, tracks, 'search'))}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
                >
                  {isCurrent && playing ? (
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                      <rect x="4" y="2.5" width="3" height="11" rx="1" />
                      <rect x="9" y="2.5" width="3" height="11" rx="1" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                      <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                    </svg>
                  )}
                </button>

                {track.artwork_small ? (
                  <img
                    src={track.artwork_small}
                    alt=""
                    loading="lazy"
                    className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                    style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)' }}
                  />
                )}

                <div className="min-w-0 flex-1">
                  <DrawerLink
                    to={`/tracks/${track.id}`}
                    className="block truncate text-sm font-medium text-ink hover:underline"
                  >
                    {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                    {track.title}
                  </DrawerLink>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {track.owner && (
                      <Link to={`/${track.owner.username}`} className="meta-caps hover:text-ink">
                        {track.owner.display_name || `@${track.owner.username}`}
                      </Link>
                    )}
                    <span className="meta-caps">
                      {track.play_count.toLocaleString()} {track.play_count === 1 ? 'play' : 'plays'}
                    </span>
                  </div>
                </div>

                <FavoriteButton
                  trackId={track.id}
                  favorited={track.favorited}
                  likeCount={track.like_count}
                  size="sm"
                />
              </Card>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Albums({ albums }: { albums: Album[] }) {
  return (
    <section>
      <h2 className="meta-caps mb-3 text-faint">{copy['noun.collection.plural']}</h2>
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-search-albums>
        {albums.map((album) => (
          <li key={album.id} data-search-album={album.id}>
            <DrawerLink to={`/albums/${album.id}`} className="group block">
              <Card className="overflow-hidden transition-colors group-hover:border-line-lit">
                <div className="aspect-square w-full overflow-hidden bg-raised">
                  {album.artwork_small ? (
                    <img src={album.artwork_small} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <div
                      className="h-full w-full"
                      style={{
                        backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 10px, var(--color-blank-alt) 10px 20px)',
                      }}
                    />
                  )}
                </div>
                <div className="p-3">
                  <p className="truncate text-sm font-medium text-ink">{album.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {album.owner && <span className="meta-caps truncate">@{album.owner.username}</span>}
                    <Chip>{album.track_count}</Chip>
                  </div>
                </div>
              </Card>
            </DrawerLink>
          </li>
        ))}
      </ul>
    </section>
  );
}
